import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import axios from "axios";

import { QUIZ_QUESTIONS, recommend } from "../data/quiz";
import { whatsappLink } from "../data/site";
import { PRICE } from "../data/collections";
import { colorOf } from "../data/site";
import MagneticButton from "../components/MagneticButton";
import Reveal from "../components/Reveal";
import { SunDoodle, Star, WhatsAppIcon } from "../components/Illustrations";
import { Heart, Flower, Scribble, Spiral } from "../components/Doodles";
import ShareCard from "../components/ShareCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BG_BY_STEP = ["#FCE6A8", "#F4C6B3", "#CCE2EF", "#D6DFCB"];

export default function Quiz() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);

  const total = QUIZ_QUESTIONS.length;
  const current = QUIZ_QUESTIONS[step];
  const result = useMemo(() => (done ? recommend(answers) : null), [done, answers]);
  const postedRef = useRef(false);

  // Push the lead once the result is ready (idempotent for StrictMode double-mount)
  useEffect(() => {
    if (!result || postedRef.current) return;
    postedRef.current = true;
    axios
      .post(`${API}/quiz-lead`, {
        age_bucket: answers.age,
        personality: answers.personality,
        color: answers.color,
        play: answers.play,
        recommendation: result.primary.slug,
        secondary: result.runnersUp.map((c) => c.slug),
      })
      .catch(() => { /* best-effort, don't break UX */ });
  }, [result, answers]);

  const choose = (val) => {
    const next = { ...answers, [current.key]: val };
    setAnswers(next);
    if (step + 1 < total) {
      setStep(step + 1);
    } else {
      setDone(true);
    }
  };

  const restart = () => { setStep(0); setAnswers({}); setDone(false); postedRef.current = false; };

  return (
    <div className="pt-28 pb-24 px-5 md:px-10 min-h-[80vh]" data-testid="quiz-page">
      {/* Header */}
      <div className="mx-auto max-w-[900px] text-center">
        <Reveal>
          <p className="eyebrow mb-3">Match my child</p>
        </Reveal>
        <Reveal delay={60}>
          <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-[-0.025em]">
            Find their <span className="italic" style={{ color: "#DD2E20" }}>perfect pair</span>
            <span className="inline-block mx-2 align-middle wiggle">
              <SunDoodle size={44} />
            </span>
            in 60&nbsp;seconds.
          </h1>
        </Reveal>
        <Reveal delay={120}>
          <Scribble w={120} className="mx-auto mt-6 opacity-80" />
        </Reveal>
      </div>

      {!done ? (
        <QuizStep
          step={step}
          total={total}
          q={current}
          onChoose={choose}
          onBack={() => setStep(Math.max(0, step - 1))}
          bg={BG_BY_STEP[step % BG_BY_STEP.length]}
          existing={answers[current.key]}
        />
      ) : (
        <ResultScreen result={result} answers={answers} onRestart={restart} />
      )}
    </div>
  );
}

function QuizStep({ step, total, q, onChoose, onBack, bg, existing }) {
  return (
    <div className="mx-auto max-w-[900px] mt-12 md:mt-16" data-testid={`quiz-step-${step}`}>
      {/* Progress */}
      <div className="flex items-center gap-4 mb-8">
        {step > 0 ? (
          <button onClick={onBack} className="inline-flex items-center gap-1 text-[13px] text-[#595955] hover:text-[#2C2C2A]" data-testid="quiz-back">
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
        ) : (
          <span className="text-[13px] text-[#595955]">Step 1 of {total}</span>
        )}
        <div className="flex-1 h-1.5 rounded-full bg-[#E8DCC4] overflow-hidden">
          <div className="h-full bg-[#2C2C2A] transition-all duration-700" style={{ width: `${((step + 1) / total) * 100}%` }} />
        </div>
        <span className="text-[13px] text-[#595955] tabular-nums" data-testid="quiz-progress">{step + 1} / {total}</span>
      </div>

      <div className="rounded-[28px] p-8 md:p-14 relative overflow-hidden" style={{ background: bg }}>
        <Flower size={60} className="absolute -top-3 right-4 wiggle hidden md:block" color="#DD2E20" center="#F4C734" />
        <Star size={22} className="absolute bottom-6 left-6 wiggle" color="#2C2C2A" />

        <h2 className="font-serif text-3xl md:text-5xl tracking-[-0.02em] leading-tight max-w-[22ch]" data-testid="quiz-question">
          {q.title}
        </h2>
        <p className="mt-3 text-[#2C2C2A]/70 text-base md:text-lg">{q.helper}</p>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {q.options.map((opt) => {
            const selected = existing === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => onChoose(opt.value)}
                className={`group flex items-center gap-4 rounded-2xl px-5 py-4 text-left transition-all duration-300
                  ${selected ? "bg-[#2C2C2A] text-[#FAFAF7]" : "bg-[#FAFAF7]/95 text-[#2C2C2A] hover:bg-white hover:shadow-[0_8px_20px_rgba(0,0,0,0.06)]"}`}
                data-testid={`quiz-option-${opt.value}`}
              >
                {opt.emoji && <span className="text-3xl shrink-0">{opt.emoji}</span>}
                {opt.swatch && (
                  <span className="w-8 h-8 rounded-full shrink-0 ring-2 ring-[#2C2C2A]/15" style={{ background: opt.swatch }} />
                )}
                <span className="font-medium text-base md:text-lg">{opt.label}</span>
                <span className="ml-auto text-lg opacity-50 group-hover:translate-x-0.5 transition-transform">→</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ResultScreen({ result, answers, onRestart }) {
  const { primary, runnersUp } = result;
  const colorName = primary.colors[0];
  const msg = `Hi SOL Kids! My quiz match was the ${primary.name} (${primary.audience.replace("For ", "")}) — would love to order one in ${colorName?.toUpperCase()} colour. (${PRICE})`;

  return (
    <div className="mx-auto max-w-[1100px] mt-12 md:mt-16" data-testid="quiz-result">
      <div className="rounded-[28px] p-8 md:p-14 relative overflow-hidden bg-[#FAFAF7] border border-[#2C2C2A]/10">
        {/* Decor */}
        <Spiral size={50} className="absolute top-6 right-10 hidden md:block" />
        <Heart size={36} className="absolute bottom-8 left-12 wiggle hidden md:block" />
        <Star size={20} className="absolute top-12 left-6 wiggle" color="#F4C734" />

        <div className="grid md:grid-cols-12 gap-8 md:gap-10 items-center">
          <div className="md:col-span-6">
            <p className="eyebrow mb-3">Your match</p>
            <h2 className="font-serif text-5xl md:text-7xl leading-[0.95] tracking-[-0.03em]">
              Meet <span className="italic" style={{ color: "#DD2E20" }}>{primary.name}</span>.
            </h2>
            <div className="mt-5 space-y-1.5 text-lg md:text-xl text-[#2C2C2A]/85 leading-snug max-w-[40ch]">
              {primary.tagline.map((t) => <p key={t}>{t}</p>)}
            </div>
            <div className="mt-6 flex items-center gap-3 text-sm">
              <span className="font-serif text-2xl">{PRICE}</span>
              <span className="text-[#595955]">·</span>
              <span className="text-[#595955]">{primary.audience}</span>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <MagneticButton>
                <a
                  href={whatsappLink(msg)}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-7 py-4 text-[15px] font-medium hover:brightness-95 transition-all"
                  data-testid="quiz-whatsapp-cta"
                >
                  <WhatsAppIcon /> Order on WhatsApp
                </a>
              </MagneticButton>
              <Link
                to={`/collection/${primary.slug}`}
                className="inline-flex items-center gap-2 rounded-full bg-[#2C2C2A] text-[#FAFAF7] px-7 py-4 text-[15px] font-medium hover:bg-[#1A1A18]"
                data-testid="quiz-view-pdp"
              >
                See full details →
              </Link>
            </div>

            <button onClick={onRestart} className="mt-5 text-[13px] text-[#595955] underline-offset-4 hover:underline" data-testid="quiz-restart">
              ↺ Take the quiz again
            </button>
          </div>

          <div className="md:col-span-6">
            <Link to={`/collection/${primary.slug}`} className="block aspect-[4/5] rounded-[24px] overflow-hidden bg-[#F3EADA] lift" data-hover="true">
              <img src={`/products/${primary.slug}-kid.jpg`} alt={primary.name} className="w-full h-full object-cover" />
            </Link>
          </div>
        </div>

        {/* Runners up */}
        <div className="mt-12 md:mt-16">
          <p className="eyebrow mb-4">You might also love</p>
          <div className="grid grid-cols-3 gap-3 md:gap-5">
            {runnersUp.map((c) => (
              <Link
                key={c.slug}
                to={`/collection/${c.slug}`}
                className="group block rounded-2xl overflow-hidden bg-[#F3EADA] lift"
                data-testid={`quiz-secondary-${c.slug}`}
              >
                <div className="aspect-[4/5] overflow-hidden">
                  <img src={`/products/${c.slug}-kid.jpg`} alt={c.name} className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-[1200ms]" />
                </div>
                <div className="px-4 py-3 flex items-center justify-between">
                  <div className="min-w-0">
                    <h3 className="font-serif text-xl truncate">{c.name}</h3>
                    <div className="flex gap-1 mt-1">
                      {c.colors.slice(0, 4).map((col) => (
                        <span key={col} className="swatch" style={{ background: colorOf(col), width: 10, height: 10 }} />
                      ))}
                    </div>
                  </div>
                  <span className="text-[12px] text-[#595955] shrink-0">{PRICE}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Shareable Instagram card */}
        <ShareCard collection={primary} answers={answers} />
      </div>
    </div>
  );
}
